import { useCallback, useEffect, useRef, useState } from 'react';
import {
  faArrowUpRightFromSquare,
  faShirt,
} from '@fortawesome/pro-light-svg-icons';
import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { queryString } from '@olivierpascal/helpers';
import { Button } from '@sixui/ui';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

import { ContentBlock } from '~/components/molecules/ContentBlock';
import { ErrorMessage } from '~/components/molecules/ErrorMessage';
import { Heading } from '~/components/molecules/Heading';
import {
  useAppPreviewDispatch,
  useAppPreviewState,
} from '~/components/organisms/AppPreview';
import { useAppQuery } from '~/lib/api/modules/Apps/hooks/useAppQuery';
import { useUpdateAppMutation } from '~/lib/api/modules/Apps/hooks/useUpdateAppMutation';
import { getAppSettings } from '~/lib/appSettings';

export type IAppMerchandisingPageProps = {
  appId: string;
  onSuccess?: (mmfId: string) => void;
};

type IMmfData = {
  type: 'MMF_ID';
  id: string;
};

export const AppMerchandisingPage: React.FC<IAppMerchandisingPageProps> = (
  props,
) => {
  const { appId, onSuccess } = props;
  const { t } = useTranslation();
  const appSettings = getAppSettings();
  const appQuery = useAppQuery({ appId });
  const updateAppMutation = useUpdateAppMutation();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const appPreviewDispatch = useAppPreviewDispatch();
  const appPreviewState = useAppPreviewState();
  const [newMmfUuid, setNewMmfUuid] = useState<string>();

  const merchMmfUrl = appQuery.data?.settings?.press?.env?.merchMMFUrl;
  const isTabActive =
    appQuery.data?.settings?.press?.env?.tabOrder?.includes('merchmmf') ??
    false;
  const mmfUuid = newMmfUuid ?? appQuery.data?.credentials?.mmf?.id;
  const iframeUrl = mmfUuid
    ? `${appSettings.mmf.loginUrl}?${queryString.build({
        mmf_uuid: appQuery.data?.credentials?.mmf?.id,
        'app-name': appQuery.data?.name,
      })}`
    : `${appSettings.mmf.registerUrl}?${queryString.build({
        'app-name': appQuery.data?.name,
      })}`;
  const iframeOrigin = new URL(iframeUrl).origin;
  const isAppPreviewReady = !!appPreviewState.iframeRef;

  useEffect(() => {
    // Update the app preview state depending of the app setup.

    if (merchMmfUrl) {
      appPreviewDispatch.setHidden(false);
      appPreviewDispatch.navigateToUrl(merchMmfUrl);
    } else {
      appPreviewDispatch.setHidden(true);
    }
  }, [appPreviewDispatch, merchMmfUrl, appId, isTabActive, isAppPreviewReady]);

  const handleMessage = useCallback(
    (message: MessageEvent): void => {
      if (message.origin !== iframeOrigin) {
        return;
      }

      if ((message.data as { type: string }).type !== 'MMF_ID') {
        return;
      }

      const data = message.data as IMmfData;
      const mmfId = data.id;
      if (!mmfId) {
        return;
      }

      const iframe = iframeRef.current;
      if (!iframe) {
        return;
      }

      const currentSrc = new URL(iframe.src);
      currentSrc.searchParams.set('mmf_uuid', mmfId);
      iframe.src = currentSrc.toString();

      void updateAppMutation.mutateAsync(
        {
          appId,
          body: {
            mmfId,
          },
        },
        { onSuccess: () => onSuccess?.(mmfId) },
      );
    },
    [iframeOrigin, updateAppMutation, appId, onSuccess],
  );

  const handleAddToExistingMmfAccount = useCallback(() => {
    const mmfId = self.crypto.randomUUID();
    setNewMmfUuid(mmfId);
    void updateAppMutation.mutateAsync(
      {
        appId,
        body: {
          mmfId,
        },
      },
      { onSuccess: () => onSuccess?.(mmfId) },
    );
  }, [updateAppMutation, appId, onSuccess, setNewMmfUuid]);

  useEffect(() => {
    window.addEventListener('message', handleMessage, false);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [handleMessage]);

  return (
    <>
      <Helmet>
        <title>{t('AppMerchandisingPage.seo.title')}</title>
      </Helmet>

      <Heading
        icon={<FontAwesomeIcon icon={faShirt} />}
        title={t('AppMerchandisingPage.title')}
        subtitle={t('AppMerchandisingPage.subtitle')}
        actions={
          <>
            <Button
              href={iframeUrl}
              target="_blank"
              variant="text"
              icon={<FontAwesomeIcon icon={faArrowUpRightFromSquare} />}
              trailingIcon
            >
              Open in a new window
            </Button>
            {!mmfUuid && (
              <Button
                icon={<FontAwesomeIcon icon={faPlus} />}
                onClick={handleAddToExistingMmfAccount}
              >
                {t('AppMerchandisingPage.addToExistingMmfAccount')}
              </Button>
            )}
          </>
        }
      />

      {iframeUrl ? (
        <ContentBlock>
          <ContentBlock.Suspense
            loading={appQuery.isLoading}
            error={appQuery.error}
            first
          >
            <ContentBlock.Iframe
              src={iframeUrl}
              title="Legacy Dashboard"
              ref={iframeRef}
            />
          </ContentBlock.Suspense>
        </ContentBlock>
      ) : (
        <ErrorMessage>Unknown error.</ErrorMessage>
      )}
    </>
  );
};
